import { AfterViewInit, Component, Inject, Input } from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { TypeResponsabilite } from '../../../perimetres/entities/type-responsabilite.entity';
import { UserService } from '../../user/services/user.service';
import { UserTLR } from '../../user/entities/user-tlr.entity';

import { PerimetreResponsabiliteService } from '../services/perimetre-responsabilite.service';
import { AgenceForPerimetreCAC } from '../entities/agence-for-perimetre-cac.entity';
import { AntenneForPerimetreCAC } from '../entities/antenne-for-perimetre-cac.entity';
import { ParcForPerimetreCAC } from '../entities/parc-for-perimetre-cac.entity';
import { SousParcForPerimetreCAC } from '../entities/sous-parc-for-perimetre-cac.entity';

class RowspannedCell<T> {
	entity!: T;
	rows: number = 1;
}

class CellRegistry<T> {
	private headerCell: RowspannedCell<T> | null = null;
	private nextCell: RowspannedCell<T> | null = null;
	
	constructor() {
	}
	
	newEntity(entity: T) {
		this.headerCell = {
			entity: entity,
			rows: 0
		};
		this.nextCell = this.headerCell;
	}
	
	duplicateLastEntity() {
		if (this.headerCell)
			this.headerCell = {
				entity: this.headerCell.entity,
				rows: 0
			}
		this.nextCell = this.headerCell;
	}
	
	getLastHeader(): RowspannedCell<T> | null {
		return this.headerCell;
	}
	
	getNextCell(): RowspannedCell<T> | null {
		let result = this.nextCell;
		this.nextCell = null;
		if (this.headerCell)
			this.headerCell.rows++;
		return result;
	}
}

class Row {
	agence!: RowspannedCell<AgenceForPerimetreCAC> | null;
	antenne!: RowspannedCell<AntenneForPerimetreCAC> | null;
	parc!: RowspannedCell<ParcForPerimetreCAC> | null;
	sousParc!: RowspannedCell<SousParcForPerimetreCAC> | null;
	codesOuvrages!: string;
	responsable!: RowspannedCell<UserTLR | null> | null;
}

@Component({
	selector: 'responsabilite-widget',
	templateUrl: 'responsabilite-widget.html',
	styleUrls: ['responsabilite-widget.css'],
})
export class ResponsabiliteWidgetComponent implements AfterViewInit {
	storageName!: string;
	title!: string;
	alt!: string;
	href!: string;
	responsabilite$ = new Subject<TypeResponsabilite>();

	// Elements de fonctionnement du tableau
	rows: Row[] = [];
	displayedColumns: string[] = ['agence', 'antenne', 'parc', 'nom', 'sousParcs', 'ouvrages', 'responsable'];
	dataSource: MatTableDataSource<Row> = new MatTableDataSource(this.rows);

	private unknownUser: UserTLR = {
		id: -1,
		deviceId: 'unknown',
		email: 'unknown',
		firstName: 'unknown',
		trigramme: 'unknown',
		idpId: 'unknown',
		lastName: 'unknown',
		phoneNumber: 'unknown',
		pwd: 'unknown',
		role: 'unknown',
		profil: 'unknown',
		habilitationCAC: false,
		habilitationCC: false,
		uid: 'unknown',
		antenneId: -1,
		agenceId: -1,
		idDiabolocom: 'unknown',
		etat: false,
		testAcces: 'unknown',
		chargeAccesId: -1
	}

	constructor(
		@Inject(PerimetreResponsabiliteService) perimetreResponsabiliteService: PerimetreResponsabiliteService,
		@Inject(UserService) userService: UserService,
	) {
		let user$ = userService.getCurrentUserTLR();
		
		let responsables$ = combineLatest([this.refreshCommandsSubject, this.responsabilite$]).pipe(
			mergeMap(
				([_, responsabilite]) => userService.getAllResponsablesTLR(responsabilite)
			)
		);
		
		responsables$.pipe(
			withLatestFrom(user$, this.responsabilite$),
			mergeMap(
				([responsables, user, responsabilite]) => {
					// Preparation de la map des responsables				
					let responsablesById = new Map<number, UserTLR>();
					for (let responsable of responsables) {
						responsablesById.set(responsable.id, responsable);
					}

					// Charger les donnees sur les agences avec les perimetres des CAC
					// TODO revoir toute la logique de la fabrication des perimetres pour avoir les CC pris en compte
					let agences$ = user.role === 'CCE' ?
						perimetreResponsabiliteService.listPerimetres(responsabilite) :
						perimetreResponsabiliteService.listPerimetresByAntenne(responsabilite, user.antenneId);
					let rows$ = agences$.pipe(
						map(
							(agences) => {
								// Ces objets servent a assurer le suivi des celulles a ajouter dans chaque colonne
								// ou un rowspan est possible								
								let agenceCellRegistry = new CellRegistry<AgenceForPerimetreCAC>();
								let antenneCellRegistry = new CellRegistry<AntenneForPerimetreCAC>();
								let parcCellRegistry = new CellRegistry<ParcForPerimetreCAC>();
								let sousParcCellRegistry = new CellRegistry<SousParcForPerimetreCAC>();
								let responsableCellRegistry = new CellRegistry<UserTLR | null>();
								
								// Codes ouvrages pour la prochaine ligne a ajouter au tableau 
								let codesOuvrages: string[] = [];
								
								// Ceci est la methode qui sert a ajouter une ligne dans le tableau
								let rows: Row[] = [];
								let addRow: () => void = () => {
									// Cas special: il n'a pas d'ouvrages notes, dans ce cas on annule
									if (codesOuvrages.length === 0)
										return;
									
									let row: Row = {
										agence: agenceCellRegistry.getNextCell(),
										antenne: antenneCellRegistry.getNextCell(),
										parc: parcCellRegistry.getNextCell(),
										sousParc: sousParcCellRegistry.getNextCell(),
										codesOuvrages: codesOuvrages.join(", "),
										responsable: responsableCellRegistry.getNextCell(),
									};
									rows.push(row);
									
									// Le sous-parc ne fait plus l'objet d'une fusion verticale
									// Donc il faut reset a sa derniere valeur apres chaque ecriture de ligne
									sousParcCellRegistry.duplicateLastEntity();
									
									// NB: quand on ajoute une ligne au tableau, on reset la liste des codes ouvrages
									codesOuvrages = [];
								};
									
								// Commencer a iterer sur tous les lements pour construire les lignes du tableau
								for (let agence of agences) {
									agenceCellRegistry.newEntity(agence);
																		
									for (let antenne of agence.antennes) {
										antenneCellRegistry.newEntity(antenne);
										
										for (let parc of antenne.parcs) {
											parcCellRegistry.newEntity(parc);
											
											for (let sousParc of parc.sousParcs) {
												sousParcCellRegistry.newEntity(sousParc);
												
												for (let ouvrage of sousParc.ouvrages) {
													this.buildUserCell(responsableCellRegistry, responsablesById, ouvrage.cacId, addRow)
													
													// Ajouter le code ouvrage a la liste
													codesOuvrages.push(ouvrage.code);
												}


												// A l'issue des iterations, la toute derniere pour le sous-parc
												// n'est pas inscrite, il faut donc le faire
												addRow();
											}
										}
									}
									
									// Par decision en daily, on ne fusionne pas les cellules CAC/CC entre les agences
									// Il faut donc faire un reset du registery correspondant sur le dernier responsable
									// declare quand on finit une agence
									responsableCellRegistry.duplicateLastEntity();
								}
	
								return rows;
							}
						)
					);

					return rows$;
				}
			),
		).subscribe(
			(rows) => {
				// Mise a jour du dataSource
				this.rows = rows;
				this.dataSource.data = rows;
			}
		);
	}
	
	private buildUserCell(cellRegistry: CellRegistry<UserTLR | null>, usersById: Map<number, UserTLR>, userId: number | null, addRow: () => void) {
		let lastHeader = cellRegistry.getLastHeader();
		let newUser = false;

		if (!lastHeader) {
			// S'il n'y a pas encore eu de header, alors le CAC est forcement nouveau
			newUser = true;
		} else {
			let lastCac = lastHeader.entity;
			if (lastCac) {
				// Si l'ouvrage precedent avait un CAC, on est sur un nouveau CAC que si les id different
				newUser = lastCac.id != userId;
			} else {
				// Si l'ouvrage precedent n'avait pas de CAC, on est sur un nouveau CAC que si le nouvel ouvrage en a un
				newUser = userId != null;
			}
		}

		// Si on est sur un nouveau CAC
		if (newUser) {
			// Ajouter le rang pour le CAC precedent
			addRow();
			
			if (userId == null) {
				cellRegistry.newEntity(null);
			} else {
				let user = usersById.get(userId);
				let nnUser = user ? user : this.unknownUser;
				cellRegistry.newEntity(nnUser);
			}
		}
	}
	
	// Liaison de l'observable servant a commander le rafraichissement des donnees
	private refreshCommandsSubject = new SubjectRelay<void>();
	@Input() set refreshCommands(obs: Observable<void> | undefined) {
		this.refreshCommandsSubject.registerSource(obs);
	}
	
	@Input() set responsabilite(value: TypeResponsabilite | undefined) {
		if (value !== undefined) {
			this.responsabilite$.next(value);
			switch(value) {
				case 'CAC':
					this.storageName="aurora_widgetCacActuel_hideContent"
					this.title="CAC actuel(s)"
					this.alt="Conna&icirc;tre tous les CAC"
					this.href="index.html#/tousPerimetres"
					break;
				case 'CC':
					this.storageName="aurora_widgetCcActuel_hideContent"
					this.title="CC actuel(s)"
					this.alt="Conna&icirc;tre tous les CC"
					this.href="index.html#/tousPerimetres"
					break;
			}
		}
	}

	ngAfterViewInit() {
		this.refreshCommandsSubject.next();
	}
}